/**
 * useFallFilter — synchronisiert den FallFilterState bidirektional mit der URL-Query.
 *
 * - Beim Laden: Zustand aus `window.location.search` parsen, damit Deep-Links funktionieren.
 * - Bei Änderung: neue Query in die URL pushen (wouter), damit Browser-Back greift.
 * - Reagiert auf externe Navigation (Back/Forward), indem es auf `useSearch` hört.
 */
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useSearch } from 'wouter';
import {
  EMPTY_FALL_FILTER,
  isFallFilterEmpty,
  parseFallFilterFromSearch,
  serializeFallFilterToSearch,
  type FallFilterState,
} from '@/lib/fall-filter-url';

export function useFallFilter(): [FallFilterState, (next: FallFilterState) => void] {
  const search = useSearch();
  const [location, setLocation] = useLocation();
  const [state, setState] = useState<FallFilterState>(() => parseFallFilterFromSearch(search));

  // Externe Navigation (Back / Forward / Deep-Link) → Zustand neu aus Query ableiten
  useEffect(() => {
    setState(parseFallFilterFromSearch(search));
  }, [search]);

  const update = useCallback(
    (next: FallFilterState) => {
      setState(next);
      const qs = serializeFallFilterToSearch(next);
      const pathOnly = location.split('?')[0];
      const target = isFallFilterEmpty(next) || qs === '' ? pathOnly : `${pathOnly}?${qs}`;
      setLocation(target, { replace: false });
    },
    [location, setLocation],
  );

  return [state, update];
}

export { EMPTY_FALL_FILTER };
